import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowDownToLine, CheckCircle2, Globe, ShieldCheck, UserPlus } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { haptic, openExternal } from '@/lib/telegram';
import { money } from '@/lib/format';
import { useI18n } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { BrandMark, CopyRow, Notice, PageTitle, Spinner } from '@/components/ui';
import type { RegisterResult } from '@/types/api';

const USERNAME_RE = /^[a-zA-Z][a-zA-Z0-9]{3,19}$/;

function Steps({ step }: { step: number }) {
    const { t } = useI18n();
    const labels = [t('register.stepTerms'), t('register.stepAccount'), t('register.stepDone')];
    return (
        <div className="flex items-center gap-2 mb-5">
            {labels.map((l, i) => (
                <div key={l} className="flex-1">
                    <div className={clsx('h-1.5 rounded-full', i <= step ? 'bg-brand-400' : 'bg-white/10')} />
                    <div className={clsx('text-[10px] mt-1', i <= step ? 'text-brand-200' : 'text-ink-muted')}>{l}</div>
                </div>
            ))}
        </div>
    );
}

export function RegisterPage() {
    const { t, te, lang } = useI18n();
    const { me, refresh } = useAuth();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [agree, setAgree] = useState(false);
    const [busy, setBusy] = useState(false);
    const [username, setUsername] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState<RegisterResult | null>(null);
    const [termsOk, setTermsOk] = useState(!!me?.termsAccepted);

    if (me?.registered && !done) return <Navigate to="/" replace />;

    const acceptTerms = async () => {
        setBusy(true); setError(null);
        try { await api.post('/terms/accept'); setTermsOk(true); haptic('success'); }
        catch (e) { setError(te(apiError(e))); haptic('error'); }
        finally { setBusy(false); }
    };

    const create = async () => {
        if (!USERNAME_RE.test(username)) { setError(t('errors.invalid_username')); haptic('error'); return; }
        setBusy(true); setError(null);
        try {
            const { data } = await api.post<RegisterResult>('/register', { username });
            setDone(data);
            haptic('success');
            await refresh();
            void qc.invalidateQueries();
        } catch (e) {
            setError(te(apiError(e)));
            haptic('error');
        } finally { setBusy(false); }
    };

    if (done) {
        return (
            <div className="space-y-4">
                <Steps step={2} />
                <div className="card-lime text-center py-6 space-y-2">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-brand-300" />
                    <h1 className="text-xl font-extrabold font-display">{t('register.successTitle')}</h1>
                    <p className="text-sm text-ink-muted">{t('register.successText')}</p>
                </div>
                <div className="space-y-2">
                    <CopyRow label={t('account.username')} value={done.username} />
                    <CopyRow label={t('account.password')} value={done.password} />
                    <CopyRow label={t('account.playerId')} value={done.playerId} />
                </div>
                <Notice tone="warn">{t('register.saveCredentials')}</Notice>
                {done.gift && (
                    <Notice tone="ok">
                        {done.gift.deposited
                            ? t('register.giftDeposited', { amount: money(done.gift.amount, me?.currency || 'BRL', lang) })
                            : t('register.giftPending', { amount: money(done.gift.amount, me?.currency || 'BRL', lang), code: done.gift.code || '' })}
                    </Notice>
                )}
                {done.referralApplied && <Notice tone="ok">{t('register.referralApplied', { code: done.referralApplied })}</Notice>}
                <button className="btn-primary w-full" onClick={() => navigate('/deposit', { replace: true })}><ArrowDownToLine className="w-4 h-4" /> {t('register.depositNow')}</button>
                {me?.brand.siteUrl && <button className="btn-secondary w-full" onClick={() => openExternal(me.brand.siteUrl)}><Globe className="w-4 h-4" /> {t('register.openSite')}</button>}
            </div>
        );
    }

    if (!termsOk) {
        return (
            <div className="space-y-4">
                <Steps step={0} />
                <PageTitle title={t('register.termsTitle')} subtitle={t('register.termsSubtitle')} />
                <div className="space-y-2">
                    {(['transfer', 'multiple', 'referral'] as const).map(k => (
                        <div key={k} className="card">
                            <div className="text-sm font-bold mb-1">{t(`terms.${k}.title`)}</div>
                            <p className="text-xs text-ink-muted leading-relaxed">{t(`terms.${k}.text`)}</p>
                        </div>
                    ))}
                </div>
                <label className="flex items-start gap-3 card cursor-pointer">
                    <input type="checkbox" className="mt-0.5 w-5 h-5 accent-[#9ef01f]" checked={agree} onChange={e => setAgree(e.target.checked)} />
                    <span className="text-sm">{t('register.agree')}</span>
                </label>
                {error && <Notice tone="warn">{error}</Notice>}
                <button className="btn-primary w-full" disabled={!agree || busy} onClick={acceptTerms}>
                    {busy ? <Spinner className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />} {t('register.accept')}
                </button>
            </div>
        );
    }

    const valid = USERNAME_RE.test(username);
    return (
        <div className="space-y-4">
            <Steps step={1} />
            <div className="flex justify-center py-2"><BrandMark size="lg" /></div>
            <PageTitle title={t('register.accountTitle')} subtitle={t('register.accountSubtitle')} />
            <div>
                <label className="label">{t('account.username')}</label>
                <input className="input font-semibold" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={20}
                       placeholder={t('register.usernamePlaceholder')} value={username}
                       onChange={e => { setUsername(e.target.value.replace(/[^a-zA-Z0-9]/g, '')); setError(null); }} />
                <p className={clsx('text-[11px] mt-1.5 px-0.5', username && !valid ? 'text-amber-200' : 'text-ink-muted')}>{t('register.usernameRules')}</p>
            </div>
            <Notice>{t('register.passwordInfo')}</Notice>
            {me?.pendingReferralCode && <Notice tone="ok">{t('register.referralPending', { code: me.pendingReferralCode })}</Notice>}
            {error && <Notice tone="warn">{error}</Notice>}
            <button className="btn-primary w-full" disabled={!valid || busy} onClick={create}>
                {busy ? <Spinner className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />} {busy ? t('register.creating') : t('register.create')}
            </button>
        </div>
    );
}
